import { z } from 'zod';

// Common validators
export const emailSchema = z
  .string()
  .min(1, 'Email is required')
  .email('Please enter a valid email address');

export const passwordSchema = z
  .string()
  .min(1, 'Password is required')
  .min(8, 'Password must be at least 8 characters')
  .regex(/[A-Z]/, 'Password must contain at least one uppercase letter')
  .regex(/[a-z]/, 'Password must contain at least one lowercase letter')
  .regex(/[0-9]/, 'Password must contain at least one number');

export const nameSchema = z
  .string()
  .min(1, 'Name is required')
  .min(2, 'Name must be at least 2 characters')
  .max(50, 'Name must be less than 50 characters')
  .regex(/^[a-zA-Z\s'-]+$/, 'Name contains invalid characters');

// Login schema
export const loginSchema = z.object({
  email: emailSchema,
  password: z.string().min(1, 'Password is required'),
  rememberMe: z.boolean().optional(),
});

export type LoginFormData = z.infer<typeof loginSchema>;

// Registration schema
export const registerSchema = z
  .object({
    email: emailSchema,
    password: passwordSchema,
    confirmPassword: z.string().min(1, 'Please confirm your password'),
    firstName: nameSchema,
    lastName: nameSchema,
    role: z.enum(['company', 'cxo', 'member']),
    agreeToTerms: z.boolean().refine((val) => val === true, {
      message: 'You must agree to the terms and conditions',
    }),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword'],
  });

export type RegisterFormData = z.infer<typeof registerSchema>;

// Brief creation schema
export const briefSchema = z.object({
  title: z
    .string()
    .min(1, 'Title is required')
    .min(5, 'Title must be at least 5 characters')
    .max(100, 'Title must be less than 100 characters'),
  description: z
    .string()
    .min(1, 'Description is required')
    .min(50, 'Description must be at least 50 characters')
    .max(2000, 'Description must be less than 2000 characters'),
  executiveFunction: z.string().min(1, 'Executive function is required'),
  engagementType: z.string().min(1, 'Engagement type is required'),
  location: z.string().optional(),
  timezone: z.string().optional(),
  budgetMin: z
    .number()
    .min(0, 'Minimum budget must be 0 or greater')
    .optional(),
  budgetMax: z
    .number()
    .min(0, 'Maximum budget must be 0 or greater')
    .optional(),
  currency: z.string().default('USD'),
  durationWeeks: z
    .number()
    .min(1, 'Duration must be at least 1 week')
    .max(52, 'Duration cannot exceed 52 weeks')
    .optional(),
  hoursPerWeek: z
    .number()
    .min(1, 'Hours per week must be at least 1')
    .max(40, 'Hours per week cannot exceed 40')
    .optional(),
  requiredSkills: z.array(z.string()).optional(),
  startDate: z.string().optional(),
  ndaRequired: z.boolean().default(false),
}).refine(
  (data) => {
    if (data.budgetMin !== undefined && data.budgetMax !== undefined) {
      return data.budgetMin <= data.budgetMax;
    }
    return true;
  },
  {
    message: 'Minimum budget cannot exceed maximum budget',
    path: ['budgetMin'],
  }
);

export type BriefFormData = z.infer<typeof briefSchema>;

// CXO Profile schema
export const cxoProfileSchema = z.object({
  firstName: nameSchema,
  lastName: nameSchema,
  title: z.string().max(100, 'Title must be less than 100 characters').optional(),
  location: z
    .string()
    .max(100, 'Location must be less than 100 characters')
    .optional(),
  timezone: z.string().optional(),
  bio: z
    .string()
    .max(2000, 'Bio must be less than 2000 characters')
    .optional(),
  linkedinProfileUrl: z
    .string()
    .url('Please enter a valid URL')
    .regex(/linkedin\.com/, 'Please enter a valid LinkedIn URL')
    .optional()
    .or(z.literal('')),
  websiteUrl: z
    .string()
    .url('Please enter a valid URL')
    .optional()
    .or(z.literal('')),
  phone: z
    .string()
    .regex(/^\+?[\d\s-()]+$/, 'Please enter a valid phone number')
    .optional()
    .or(z.literal('')),
  executiveFunctions: z.array(z.string()).min(1, 'Select at least one function'),
  industries: z.array(z.string()).optional(),
  skills: z.array(z.string()).min(1, 'Select at least one skill'),
  hourlyRate: z
    .number()
    .min(0, 'Hourly rate must be 0 or greater')
    .max(1000, 'Hourly rate cannot exceed $1000')
    .optional(),
  monthlyRetainer: z
    .number()
    .min(0, 'Monthly retainer must be 0 or greater')
    .max(50000, 'Monthly retainer cannot exceed $50,000')
    .optional(),
  availability: z.enum(['immediate', 'two_weeks', 'one_month', 'flexible']).optional(),
});

export type CXOProfileFormData = z.infer<typeof cxoProfileSchema>;

// Company Profile schema
export const companyProfileSchema = z.object({
  companyName: z
    .string()
    .min(1, 'Company name is required')
    .min(2, 'Company name must be at least 2 characters')
    .max(100, 'Company name must be less than 100 characters'),
  website: z
    .string()
    .url('Please enter a valid URL')
    .optional()
    .or(z.literal('')),
  industry: z.string().optional(),
  companySize: z.string().optional(),
  description: z
    .string()
    .max(1000, 'Description must be less than 1000 characters')
    .optional(),
});

export type CompanyProfileFormData = z.infer<typeof companyProfileSchema>;

// Message schema
export const messageSchema = z.object({
  content: z
    .string()
    .min(1, 'Message cannot be empty')
    .max(2000, 'Message must be less than 2000 characters'),
});

export type MessageFormData = z.infer<typeof messageSchema>;

// Helper function to validate form data
export function validateForm<T>(
  schema: z.ZodSchema<T>,
  data: unknown
): { success: true; data: T } | { success: false; errors: Record<string, string> } {
  const result = schema.safeParse(data);
  
  if (result.success) {
    return { success: true, data: result.data };
  }
  
  const errors: Record<string, string> = {};
  result.error.issues.forEach((issue) => {
    const path = issue.path.join('.');
    errors[path] = issue.message;
  });
  
  return { success: false, errors };
}

// Helper function to validate a single field
export function validateField<T>(
  schema: z.ZodSchema<T>,
  field: string,
  value: unknown
): string | undefined {
  const result = schema.safeParse(value);
  
  if (!result.success) {
    const issue = result.error.issues.find((i) => i.path[0] === field);
    return issue?.message;
  }
  
  return undefined;
}
